import {
  createSseParser,
  type AccountConnection,
  type AccountLookup,
  type ChatStreamEvent,
  type ConversationDetail,
  type ConversationSummary,
  type EmbedSnippetResponse,
  type Fact,
  type FactStatus,
  type Gap,
  type GapStatus,
  type AcceptInviteRequest,
  type CreateInviteRequest,
  type CreateInviteResponse,
  type MeResponse,
  type PlatformTenantDetail,
  type PlatformTenantSummary,
  type SignupRequest,
  type TeamResponse,
  type IdentitySecretResponse,
  type TenantSettings,
  type TestAccountLookupRequest,
  type TestAccountLookupResponse,
  type TranscriptMessage,
  type UpdateAccountConnectionRequest,
  type VisitorMessage,
  type VisitorMessageStatus,
} from '@stonelink/shared';

/**
 * Console API client.
 *
 * Same-origin in both dev (Vite proxy) and production (nginx), so the session
 * cookie rides along with `credentials: 'include'` and no token handling is
 * needed in the browser at all.
 */

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(path, {
    credentials: 'include',
    headers: init.body ? { 'Content-Type': 'application/json' } : {},
    ...init,
  });

  if (response.status === 204) return undefined as T;

  const text = await response.text();
  const body = text ? (JSON.parse(text) as unknown) : null;

  if (!response.ok) {
    const error = (body as { error?: { code?: string; message?: string } } | null)?.error;
    throw new ApiError(
      response.status,
      error?.code ?? 'unknown',
      error?.message ?? `Request failed (${response.status})`,
    );
  }
  return body as T;
}

const json = (value: unknown): string => JSON.stringify(value);

export const api = {
  /* ---------------------------------------------------------------- auth -- */
  login: (email: string, password: string) =>
    request<MeResponse>('/v1/auth/login', { method: 'POST', body: json({ email, password }) }),
  logout: () => request<{ ok: boolean }>('/v1/auth/logout', { method: 'POST' }),
  me: () => request<MeResponse>('/v1/auth/me'),
  signup: (input: SignupRequest) =>
    request<MeResponse>('/v1/signup', { method: 'POST', body: json(input) }),

  /* ------------------------------------------------------------------ team -- */
  getTeam: () => request<TeamResponse>('/v1/team'),
  createInvite: (input: CreateInviteRequest) =>
    request<CreateInviteResponse>('/v1/team/invites', { method: 'POST', body: json(input) }),
  revokeInvite: (id: string) => request<void>(`/v1/team/invites/${id}`, { method: 'DELETE' }),
  removeMember: (id: string) => request<void>(`/v1/team/members/${id}`, { method: 'DELETE' }),
  acceptInvite: (input: AcceptInviteRequest) =>
    request<MeResponse>('/v1/team/accept', { method: 'POST', body: json(input) }),

  /* ------------------------------------------------------------ settings -- */
  getSettings: () => request<TenantSettings>('/v1/settings'),
  updateSettings: (patch: Partial<TenantSettings>) =>
    request<TenantSettings>('/v1/settings', { method: 'PATCH', body: json(patch) }),
  embedSnippet: () => request<EmbedSnippetResponse>('/v1/embed-snippet'),

  /* --------------------------------------------------------------- facts -- */
  listFacts: (options: { q?: string; status?: FactStatus } = {}) => {
    const params = new URLSearchParams();
    if (options.q) params.set('q', options.q);
    if (options.status) params.set('status', options.status);
    const query = params.toString();
    return request<Fact[]>(`/v1/facts${query ? `?${query}` : ''}`);
  },
  createFact: (content: string) =>
    request<Fact>('/v1/facts', { method: 'POST', body: json({ content, source: 'manual' }) }),
  updateFact: (id: string, patch: { content?: string; status?: FactStatus }) =>
    request<Fact>(`/v1/facts/${id}`, { method: 'PATCH', body: json(patch) }),
  deleteFact: (id: string) => request<void>(`/v1/facts/${id}`, { method: 'DELETE' }),

  /* ---------------------------------------------------------------- gaps -- */
  listGaps: (status?: GapStatus) => request<Gap[]>(`/v1/gaps${status ? `?status=${status}` : ''}`),
  resolveGap: (id: string, content: string) =>
    request<{ gap: Gap; fact: Fact }>(`/v1/gaps/${id}/resolve`, {
      method: 'POST',
      body: json({ content }),
    }),
  dismissGap: (id: string) => request<Gap>(`/v1/gaps/${id}/dismiss`, { method: 'POST' }),

  /* ----------------------------------------------------------- messages -- */
  listVisitorMessages: (status?: VisitorMessageStatus) =>
    request<VisitorMessage[]>(`/v1/visitor-messages${status ? `?status=${status}` : ''}`),
  setVisitorMessageStatus: (id: string, status: VisitorMessageStatus) =>
    request<VisitorMessage>(`/v1/visitor-messages/${id}`, {
      method: 'PATCH',
      body: json({ status }),
    }),

  /* ------------------------------------------------------ conversations -- */
  listConversations: (channel?: 'widget' | 'training') =>
    request<ConversationSummary[]>(`/v1/conversations${channel ? `?channel=${channel}` : ''}`),
  getConversation: (id: string) => request<ConversationDetail>(`/v1/conversations/${id}`),

  /* --------------------------------------------------- account connector -- */
  getAccountConnection: () => request<AccountConnection>('/v1/account-connection'),
  updateAccountConnection: (patch: UpdateAccountConnectionRequest) =>
    request<AccountConnection>('/v1/account-connection', { method: 'PATCH', body: json(patch) }),
  /** Returns the secret in the clear. Shown once and never retrievable again. */
  rotateIdentitySecret: () =>
    request<IdentitySecretResponse>('/v1/account-connection/identity-secret', { method: 'POST' }),
  refreshManifest: () =>
    request<AccountConnection>('/v1/account-connection/manifest/refresh', { method: 'POST' }),
  testAccountLookup: (input: TestAccountLookupRequest) =>
    request<TestAccountLookupResponse>('/v1/account-connection/test', {
      method: 'POST',
      body: json(input),
    }),
  listAccountLookups: (limit = 25) =>
    request<AccountLookup[]>(`/v1/account-lookups?limit=${limit}`),

  /* ------------------------------------------------------ platform admin -- */
  listPlatformTenants: () => request<PlatformTenantSummary[]>('/v1/platform/tenants'),
  getPlatformTenant: (id: string) => request<PlatformTenantDetail>(`/v1/platform/tenants/${id}`),
  setPlatformTenantStatus: (id: string, status: 'active' | 'disabled') =>
    request<PlatformTenantDetail>(`/v1/platform/tenants/${id}/status`, {
      method: 'POST',
      body: json({ status }),
    }),

  /* ------------------------------------------------------------ training -- */
  trainingSession: () =>
    request<{ conversationId: string; messages: TranscriptMessage[] }>('/v1/training/session', {
      method: 'POST',
    }),
  newTrainingSession: () =>
    request<{ conversationId: string; messages: TranscriptMessage[] }>('/v1/training/session/new', {
      method: 'POST',
    }),

  /** Streams a training turn, invoking `onEvent` per SSE frame. */
  async trainingChat(
    input: { message: string; conversationId?: string },
    onEvent: (event: ChatStreamEvent) => void,
  ): Promise<void> {
    const response = await fetch('/v1/training/chat', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: json(input),
    });

    if (!response.ok || !response.body) {
      onEvent({
        type: 'error',
        code: String(response.status),
        message: 'The trainer is unavailable right now. Please try again.',
      });
      return;
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    const parse = createSseParser();

    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      for (const event of parse(decoder.decode(value, { stream: true }))) onEvent(event);
    }
  },
};
